import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    NotificationFacade,
    PaymentMethodsFacade,
    PurchaseFacade,
    ShoppingCartFacade,
    ShoppingCartState
} from '@ridango/components';
import {
    AdyenResultStatus,
    AdyenSession,
    BaseComponent,
    CartStatus,
    CoreFacade,
    Language,
    ShoppingCart
} from '@ridango/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { clearCartState, loadCartState } from '../../localStorage';
import { TranslateService } from '@ngx-translate/core';

@Component({
    templateUrl: './adyen-payment-page.component.html'
})
export class AdyenPaymentPageComponent extends BaseComponent implements OnInit, OnDestroy {
    selectedLanguage$: Observable<Language> = this.coreFacade.selectedLanguage$;
    adyenSession$: Observable<AdyenSession> = this.paymentFacade.adyenSession$;
    paymentError$: Observable<Error> = this.paymentFacade.paymentError$;
    cart$: Observable<ShoppingCartState> = this.cartFacade.cart$;
    steps$: Observable<any[]> = this.purchaseFacade.purchaseSteps$;
    activeStep$: Observable<number> = this.purchaseFacade.activePurchaseStep$;
    navigateAwaySelection$: Subject<boolean> = new Subject<boolean>();

    cart: ShoppingCart;
    showNavigationModal = false;
    redirectedPayment;

    paymentResult;
    isPaymentChecked = false;
    selectedLanguageCode: string;

    constructor(
        private coreFacade: CoreFacade,
        private router: Router,
        private route: ActivatedRoute,
        private cartFacade: ShoppingCartFacade,
        private purchaseFacade: PurchaseFacade,
        private paymentFacade: PaymentMethodsFacade,
        private notifications: NotificationFacade,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.notifications.removeAll();
        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            this.redirectedPayment = {
                sessionId: params['sessionId'],
                redirectResult: params['redirectResult']
            };
        });

        this.selectedLanguage$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((language) => (this.selectedLanguageCode = language.code));

        this.adyenSession$.pipe(takeUntil(this.componentDestroyed$)).subscribe((session) => {
            if (
                session.checkedPaymentStatus &&
                session.checkedPaymentStatus.status === CartStatus.VALID &&
                this.paymentResult
            ) {
                this.isPaymentChecked = true;
                this.router.navigate([this.selectedLanguageCode, 'purchase', 'result'], {
                    queryParams: { resultCode: this.paymentResult.resultCode }
                });
            }
        });

        this.paymentError$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            if (error) {
                if (error.message === 'MAX_RETRY_ATTEMPT_EXCEEDED') {
                    this.isPaymentChecked = true;

                    this.notifications.info(this.translate.instant('ADYEN_PAYMENT_PENDING_RECEIVED'), {
                        showAfterRouteChange: true
                    });
                    this.router.navigate([this.selectedLanguageCode, 'purchase', 'result'], {
                        queryParams: { resultCode: AdyenResultStatus.RECEIVED }
                    });
                } else {
                    window.scrollTo(0, 0);
                    this.notifications.error(this.translate.instant('UNKNOWN_ERROR'));
                }
            }
        });

        this.cart$.pipe(takeUntil(this.componentDestroyed$)).subscribe((c) => {
            if (!c.shopping_cart_id) {
                this.router.navigate([this.selectedLanguageCode, 'cart']);
            }
        });
    }

    managePayment(result) {
        this.paymentResult = result;

        this.cart$.pipe(take(2)).subscribe((c) => {
            this.cart = c;
            if (!c.shopping_cart_id) {
                const cart = loadCartState();
                this.cartFacade.hydrateState(cart);
            }

            if (this.cart.jwt_token) {
                this.paymentFacade.fetchPaymentStatus(this.cart.jwt_token);
            }
        });
    }

    canDeactivate() {
        this.showNavigationModal = true;
        if (!this.paymentResult || this.isPaymentChecked) {
            return true;
        }
        return this.navigateAwaySelection$;
    }

    onCloseNavigationModal() {
        this.showNavigationModal = false;
        this.navigateAwaySelection$.next(false);
    }

    onConfirmNavigationModal() {
        this.cartFacade.deleteCart();
        this.purchaseFacade.unsetShoppingCart();
        this.navigateAwaySelection$.next(true);
        clearCartState();
        this.showNavigationModal = false;
    }
}
